import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import { fetchTrendingPosts, generateCodeVerifier, generateCodeChallenge, exchangeCodeForToken, getUser, fetchReplies, fetchRepliesForPost, postReplyToTweet, fetchPopularLists, fetchListPosts, clientId } from './api';
import TweetMedia from './TweetMedia';
import { searchEmojis } from './emojiData';
import { formatTimestamp } from './timeUtils';
import { Tweet } from 'react-tweet';

function App() {
  const [topic, setTopic] = useState('news');
  const [posts, setPosts] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [source, setSource] = useState('');
  const [showLogs, setShowLogs] = useState(false);
  const [likedPosts, setLikedPosts] = useState(new Set());
  const [postReplies, setPostReplies] = useState({});
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replyStatus, setReplyStatus] = useState(null); // 'sending', 'success', 'error'
  const [replyMedia, setReplyMedia] = useState(null);
  const [replyMediaPreview, setReplyMediaPreview] = useState(null);
  const [emojiSuggestions, setEmojiSuggestions] = useState([]);
  const [selectedEmojiIndex, setSelectedEmojiIndex] = useState(0);
  const [emojiQuery, setEmojiQuery] = useState('');
  const [emojiQueryStart, setEmojiQueryStart] = useState(-1);
  
  // Lists state
  const [popularLists, setPopularLists] = useState([]);
  const [selectedList, setSelectedList] = useState('1869744713948942625'); // Default list
  const [manualListId, setManualListId] = useState('');
  const [showRepliesFor, setShowRepliesFor] = useState(new Set());
  const [loadingRepliesFor, setLoadingRepliesFor] = useState(new Set());
  
  // PRO view state
  const [proLists, setProLists] = useState({});
  const [proCustomListId, setProCustomListId] = useState('');
  const [loadingProLists, setLoadingProLists] = useState(false);
  
  // Twitterapi.io login state
  const [showTwitterApiLogin, setShowTwitterApiLogin] = useState(false);
  const [twitterApiCreds, setTwitterApiCreds] = useState({
    username: '',
    email: '',
    password: '',
    apiKey: '',
    proxy: '',
    totpSecret: ''
  });
  const [twitterApiUser, setTwitterApiUser] = useState(null);

  const fetchPosts = useCallback(async (currentTopic, forceRefresh = false) => {
    setLoading(true);
    const result = await fetchTrendingPosts(currentTopic, forceRefresh);
    
    // Update logs and source
    setLogs(result.logs || []);
    setSource(result.source || 'unknown');
    
    // Only update posts if we got new ones, otherwise keep old posts visible
    if (result.posts && result.posts.length > 0) {
      // Calculate viral rating and adjust sizes for tablet landscape
      const postsWithViralRating = result.posts.map(post => ({
        ...post,
        viralRating: post.likes + (post.retweets * 2) + (post.replies * 0.5)
      }));
      
      // Sort by viral rating to identify top posts
      const sortedByViral = [...postsWithViralRating].sort((a, b) => b.viralRating - a.viralRating);
      
      // Calculate how many should be large (1/3 of total)
      const largeCount = Math.ceil(postsWithViralRating.length / 3);
      const topViralIds = new Set(sortedByViral.slice(0, largeCount).map(p => p.id));
      
      // Adjust sizes: only top 1/3 by viral rating get 'large' size
      const adjustedPosts = postsWithViralRating.map(post => {
        if (topViralIds.has(post.id) && post.image) {
          return { ...post, size: 'large' };
        } else if (post.text.split(/\s+/).length <= 50 && post.likes > 1000) {
          return { ...post, size: 'medium' };
        } else if (post.text.split(/\s+/).length <= 50) {
          return { ...post, size: 'small' };
        } else {
          return { ...post, size: 'medium' };
        }
      });
      
      setPosts(adjustedPosts);
      
      // Fetch replies for only the first 5 posts to avoid API rate limits
      const postsToFetchReplies = adjustedPosts.slice(0, 20);
      postsToFetchReplies.forEach(async (post) => {
        const replies = await fetchReplies(post.id);
        if (replies.length > 0) {
          setPostReplies(prev => ({
            ...prev,
            [post.id]: replies
          }));
        }
      });
    } else {
      // Use functional setState to check current posts without dependency
      setPosts(prev => prev.length === 0 ? [] : prev);
    }
    
    setLoading(false);
    
    // Auto-show logs if manual refresh
    if (forceRefresh) {
      setShowLogs(true);
    }
  }, []); // No dependencies needed - uses functional setState

  useEffect(() => {
    fetchPosts(topic);
    const interval = setInterval(() => {
      fetchPosts(topic);
    }, 30000000); // 5 minutes
    return () => clearInterval(interval);
  }, [topic, fetchPosts]);

  // Fetch lists when user logs in or when Lists/PRO tab is selected
  useEffect(() => {
    const loadLists = async () => {
      if (topic === 'lists' || topic === 'pro') {
          // Fetch popular lists
          const lists = await fetchPopularLists();
          setPopularLists(lists);
      }
    };
    
    loadLists();
  }, [topic, user]);

  // Fetch posts from selected list
  useEffect(() => {
    const loadListPosts = async () => {
      if (topic === 'lists' && selectedList) {
        setLoading(true);
        const result = await fetchListPosts(selectedList, !!user);
        
        setLogs(result.logs || []);
        setSource(result.source || 'unknown');
        
        if (result.posts && result.posts.length > 0) {
          const postsWithViralRating = result.posts.map(post => ({
            ...post,
            viralRating: post.likes + (post.retweets * 2) + (post.replies * 0.5)
          }));
          
          setPosts(postsWithViralRating);
          
          // For posts with topReplies, add them to postReplies state
          postsWithViralRating.forEach(post => {
            if (post.topReplies && post.topReplies.length > 0) {
              setPostReplies(prev => ({
                ...prev,
                [post.id]: post.topReplies
              }));
            }
          });
        } else {
          setPosts([]);
        }
        
        setLoading(false);
      }
    };
    
    loadListPosts();
  }, [topic, selectedList, user]);
  
  // Load PRO view - fetch posts for multiple lists
  useEffect(() => {
    const loadProLists = async () => {
      if (topic === 'pro') {
        setLoadingProLists(true);
        
        // Get first 5 lists from popularLists
        const listsToFetch = popularLists.slice(0, 5);
        
        // Fetch all lists in parallel
        const results = await Promise.all(
          listsToFetch.map(list => fetchListPosts(list.id, !!user))
        );
        
        // Store posts by list ID
        const newProLists = {};
        listsToFetch.forEach((list, index) => {
          const result = results[index];
          if (result.posts && result.posts.length > 0) {
            newProLists[list.id] = {
              listInfo: list,
              posts: result.posts.slice(0, 20).map(post => ({
                ...post,
                viralRating: post.likes + (post.retweets * 2) + (post.replies * 0.5)
              }))
            };
            
            // Store replies for these posts
            result.posts.forEach(post => {
              if (post.topReplies && post.topReplies.length > 0) {
                setPostReplies(prev => ({
                  ...prev,
                  [post.id]: post.topReplies
                }));
              }
            });
          }
        });
        
        setProLists(newProLists);
        setLoadingProLists(false);
      }
    };
    
    if (popularLists.length > 0) {
      loadProLists();
    }
  }, [topic, popularLists, user]);
  
  // Add custom list to PRO view
  const handleAddProCustomList = async () => {
    if (!proCustomListId || proLists[proCustomListId]) return;
    
    setLoadingProLists(true);
    const result = await fetchListPosts(proCustomListId, !!user);
    
    if (result.posts && result.posts.length > 0) {
      setProLists(prev => ({
        ...prev,
        [proCustomListId]: {
          listInfo: {
            id: proCustomListId,
            name: `List ${proCustomListId}`,
            owner_username: 'Unknown'
          },
          posts: result.posts.slice(0, 20).map(post => ({
            ...post,
            viralRating: post.likes + (post.retweets * 2) + (post.replies * 0.5)
          }))
        }
      }));
      
      // Store replies
      result.posts.forEach(post => {
        if (post.topReplies && post.topReplies.length > 0) {
          setPostReplies(prev => ({
            ...prev,
            [post.id]: post.topReplies
          }));
        }
      });
      
      setProCustomListId('');
    }
    
    setLoadingProLists(false);
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    if (code) {
      const codeVerifier = localStorage.getItem('code_verifier');
      exchangeCodeForToken(code, codeVerifier)
        .then(async (token) => {
          console.log('Got token:', token);
          
          // Set cookie with 1 year expiration
          const expirationDate = new Date();
          expirationDate.setFullYear(expirationDate.getFullYear() + 1);
          document.cookie = `access_token=${token}; expires=${expirationDate.toUTCString()}; path=/; SameSite=Lax`;
          
          // Also keep in localStorage for backward compatibility
          localStorage.setItem('access_token', token);
          
          // CRITICAL: Extract personalization_id from cookies set by twitter.com
          const cookies = document.cookie.split(';');
          let personalizationId = null;
          
          for (let cookie of cookies) {
            const [name, value] = cookie.trim().split('=');
            if (name === 'personalization_id') {
              personalizationId = decodeURIComponent(value.replace(/"/g, ''));
              break;
            }
          }
          
          if (personalizationId) {
            // Set cookie with 1 year expiration
            document.cookie = `x_personalization_id=${personalizationId}; expires=${expirationDate.toUTCString()}; path=/; SameSite=Lax`;
            localStorage.setItem('personalization_id', personalizationId);
            console.log('Saved personalization_id:', personalizationId);
          } else {
            console.warn('personalization_id cookie not found! Some actions may fail.');
          }
          
          try {
            const userData = await getUser(token);
            console.log('Got user data:', userData);
            
            // Save user data to cookie as well
            const userDataStr = JSON.stringify({
              username: userData.username,
              id: userData.id,
              profile_image_url: userData.profile_image_url
            });
            document.cookie = `user_data=${encodeURIComponent(userDataStr)}; expires=${expirationDate.toUTCString()}; path=/; SameSite=Lax`;
            
            setUser({ 
              token, 
              name: userData.username, 
              id: userData.id,
              profileImage: userData.profile_image_url 
            });
            window.history.replaceState({}, document.title, window.location.pathname);
          } catch (error) {
            console.error('Error fetching user:', error);
            console.error('Error details:', error.response?.data);
            // Still set user with just token so they can use the app
            setUser({ token, name: 'User', id: null });
            window.history.replaceState({}, document.title, window.location.pathname);
          }
        })
        .catch(error => {
          console.error('Token exchange failed:', error);
          alert('Login failed. Please try again.');
        });
    } else {
      // Load from cookies first, then fallback to localStorage
      const getCookie = (name) => {
        const cookies = document.cookie.split(';');
        for (let cookie of cookies) {
          const [cookieName, cookieValue] = cookie.trim().split('=');
          if (cookieName === name) {
            return decodeURIComponent(cookieValue);
          }
        }
        return null;
      };
      
      const token = getCookie('access_token') || localStorage.getItem('access_token');
      const savedPersonalizationId = getCookie('x_personalization_id') || localStorage.getItem('personalization_id');
      const savedUserData = getCookie('user_data');
      
      if (token) {
        if (savedUserData) {
          // Use cached user data from cookie
          try {
            const userData = JSON.parse(savedUserData);
            console.log('Loaded user data from cookie:', userData);
            setUser({ 
              token, 
              name: userData.username, 
              id: userData.id,
              profileImage: userData.profile_image_url 
            });
            
            // Ensure personalization_id is in localStorage
            if (savedPersonalizationId && !localStorage.getItem('personalization_id')) {
              localStorage.setItem('personalization_id', savedPersonalizationId);
            }
          } catch (error) {
            console.error('Error parsing user data from cookie:', error);
            // Fall through to API fetch
          }
        }
        
        // Always try to refresh user data from API in background
        if (savedPersonalizationId) {
          getUser(token)
            .then(userData => {
              console.log('Refreshed user data from API:', userData);
              setUser({ 
                token, 
                name: userData.username, 
                id: userData.id,
                profileImage: userData.profile_image_url 
              });
              
              // Update cookie with fresh data
              const expirationDate = new Date();
              expirationDate.setFullYear(expirationDate.getFullYear() + 1);
              const userDataStr = JSON.stringify({
                username: userData.username,
                id: userData.id,
                profile_image_url: userData.profile_image_url
              });
              document.cookie = `user_data=${encodeURIComponent(userDataStr)}; expires=${expirationDate.toUTCString()}; path=/; SameSite=Lax`;
            })
            .catch(error => {
              console.error('Error loading saved session:', error);
              console.error('Error details:', error.response?.data);
              // If we don't have cached user data, clear everything
              if (!savedUserData) {
                localStorage.removeItem('access_token');
                localStorage.removeItem('personalization_id');
                document.cookie = 'access_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
                document.cookie = 'x_personalization_id=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
                document.cookie = 'user_data=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
              }
            });
        }
      }
    }
    
    // Load twitterapi.io session from sessionStorage
    const savedTwitterApiCreds = sessionStorage.getItem('twitterapi_creds');
    if (savedTwitterApiCreds) {
      try {
        const creds = JSON.parse(savedTwitterApiCreds);
        setTwitterApiUser({
          username: creds.username,
          loginType: 'twitterapi'
        });
      } catch (error) {
        console.error('Error loading twitterapi.io session:', error);
        sessionStorage.removeItem('twitterapi_creds');
      }
    }
  }, []);

  const handleLogin = async () => {
    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    localStorage.setItem('code_verifier', codeVerifier);
    
    // Use the correct redirect URI based on hostname
    let redirectUri;
    if (window.location.hostname.includes('localhost') || window.location.hostname === '127.0.0.1') {
      redirectUri = 'http://localhost:3000';
    } else if (window.location.hostname === 'x-newspaper.com') {
      redirectUri = 'https://x-newspaper.com';
    } else {
      // Fallback for pages.dev
      redirectUri = 'https://x-newspaper.pages.dev';
    }
    
    // Store the redirect URI so we can use the same one during token exchange
    localStorage.setItem('redirect_uri', redirectUri);
    
    // Note: personalization_id removed as it was causing issues with Twitter's OAuth flow
    const authUrl = `https://twitter.com/i/oauth2/authorize?response_type=code&client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=tweet.read%20tweet.write%20users.read%20like.read%20like.write&code_challenge=${codeChallenge}&code_challenge_method=S256&state=state`;
    window.location.href = authUrl;
  };

  const handleReply = (event, tweetId) => {
    event.stopPropagation();
    
    if (!user && !twitterApiUser) {
      // If not logged in, open X.com
      const replyUrl = `https://twitter.com/intent/tweet?in_reply_to=${tweetId}`;
      window.open(replyUrl, '_blank', 'width=600,height=600');
      return;
    }
    
    // If logged in (either way), toggle reply textarea
    if (replyingTo === tweetId) {
      setReplyingTo(null);
      setReplyText('');
      setReplyStatus(null);
      setReplyMedia(null);
      setReplyMediaPreview(null);
    } else {
      setReplyingTo(tweetId);
      setReplyText('');
      setReplyStatus(null);
      setReplyMedia(null);
      setReplyMediaPreview(null);
    }
  };
  
  const handleMediaSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Only allow media for twitterapi.io users (X OAuth requires Elevated Access)
      if (!twitterApiUser && user) {
        alert('Media upload requires Elevated API Access. Please apply at developer.twitter.com');
        return;
      }
      
      setReplyMedia(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setReplyMediaPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  const handleRemoveMedia = (event) => {
    event.stopPropagation();
    setReplyMedia(null);
    setReplyMediaPreview(null);
  };
  
  const handleReplyTextChange = (e) => {
    const text = e.target.value;
    setReplyText(text);
    
    // Detect emoji shortcode pattern :text:
    const cursorPos = e.target.selectionStart;
    const textBeforeCursor = text.substring(0, cursorPos);
    const colonMatch = textBeforeCursor.match(/:(\w+)$/);
    
    if (colonMatch) {
      const query = colonMatch[1];
      const startPos = cursorPos - query.length - 1;
      
      if (query.length >= 2) {
        const suggestions = searchEmojis(query);
        setEmojiSuggestions(suggestions);
        setEmojiQuery(query);
        setEmojiQueryStart(startPos);
        setSelectedEmojiIndex(0);
      } else {
        setEmojiSuggestions([]);
      }
    } else {
      setEmojiSuggestions([]);
    }
  };
  
  const insertEmoji = (emoji) => {
    if (emojiQueryStart === -1) return;
    
    // Replace :query with emoji
    const beforeQuery = replyText.substring(0, emojiQueryStart);
    const afterQuery = replyText.substring(emojiQueryStart + emojiQuery.length + 1);
    const newText = beforeQuery + emoji + ' ' + afterQuery;
    
    setReplyText(newText);
    setEmojiSuggestions([]);
    setEmojiQuery('');
    setEmojiQueryStart(-1);
  };
  
  const handleEmojiKeyDown = (e) => {
    if (emojiSuggestions.length === 0) return;
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedEmojiIndex(prev => 
        prev < emojiSuggestions.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedEmojiIndex(prev => 
        prev > 0 ? prev - 1 : emojiSuggestions.length - 1
      );
    } else if (e.key === 'Enter' && emojiSuggestions.length > 0) {
      e.preventDefault();
      insertEmoji(emojiSuggestions[selectedEmojiIndex].emoji);
    } else if (e.key === 'Escape') {
      setEmojiSuggestions([]);
    }
  };
  
  const handleSendReply = async (event, tweetId) => {
    event.stopPropagation();
    
    if (!replyText.trim()) return;
    
    setReplyStatus('sending');
    
    try {
      if (twitterApiUser) {
        // Use twitterapi.io endpoint
        const creds = JSON.parse(sessionStorage.getItem('twitterapi_creds'));
        
        let mediaData = null;
        if (replyMedia) {
          // Convert file to base64
          const reader = new FileReader();
          mediaData = await new Promise((resolve, reject) => {
            reader.onloadend = () => {
              // Get base64 string without data URL prefix
              const base64 = reader.result.split(',')[1];
              resolve(base64);
            };
            reader.onerror = reject;
            reader.readAsDataURL(replyMedia);
          });
        }
        
        const response = await fetch('/api/twitterapiReply', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tweetId,
            text: replyText,
            username: creds.username,
            email: creds.email,
            password: creds.password,
            apiKey: creds.apiKey,
            proxy: creds.proxy,
            totpSecret: creds.totpSecret,
            mediaData
          })
        });
        
        if (!response.ok) {
          throw new Error('Failed to send reply via twitterapi.io');
        }
      } else {
        // Use X OAuth endpoint
        await postReplyToTweet(tweetId, replyText, user.token, user.id, replyMedia);
      }
      
      setReplyStatus('success');
      setReplyText('');
      setReplyMedia(null);
      setReplyMediaPreview(null);
      
      // Hide reply box and show success message
      setTimeout(() => {
        setReplyingTo(null);
        setReplyStatus(null);
      }, 2000);
    } catch (error) {
      setReplyStatus('error');
      console.error('Reply error:', error);
    }
  };

  const handleLike = async (postId, event) => {
    event.stopPropagation();
    
    if (twitterApiUser) {
      // Use twitterapi.io endpoint
      try {
        const creds = JSON.parse(sessionStorage.getItem('twitterapi_creds'));
        
        const response = await fetch('/api/twitterapiLike', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tweetId: postId,
            username: creds.username,
            email: creds.email,
            password: creds.password,
            apiKey: creds.apiKey,
            proxy: creds.proxy,
            totpSecret: creds.totpSecret
          })
        });
        
        if (!response.ok) {
          throw new Error('Failed to like tweet via twitterapi.io');
        }
        
        // Optimistic update
        setLikedPosts(prev => new Set([...prev, postId]));
        setPosts(prevPosts => prevPosts.map(post => {
          if (post.id === postId) {
            return { ...post, likes: post.likes + 1 };
          }
          return post;
        }));
      } catch (error) {
        console.error('Like error:', error);
        alert('Failed to like tweet. Please try again.');
      }
    } else {
      // Use X intent URL to like (opens in popup window)
      const likeUrl = `https://twitter.com/intent/favorite?tweet_id=${postId}`;
      window.open(likeUrl, 'likeWindow', 'width=600,height=600');
      
      // Optimistic update (user will like in the popup)
      setLikedPosts(prev => new Set([...prev, postId]));
      setPosts(prevPosts => prevPosts.map(post => {
        if (post.id === postId) {
          return { ...post, likes: post.likes + 1 };
        }
        return post;
      }));
    }
  };
  
  const openOnX = (tweetId, authorUsername, event) => {
    // Don't trigger if clicking on interactive elements
    if (event && (
      event.target.closest('.stat-item') ||
      event.target.closest('.author-username') ||
      event.target.closest('.post-actions')
    )) {
      return;
    }
    const tweetUrl = `https://twitter.com/${authorUsername}/status/${tweetId}`;
    window.open(tweetUrl, '_blank');
  };

  const handleLogout = () => {
    setUser(null);
    // Clear localStorage
    localStorage.removeItem('access_token');
    localStorage.removeItem('personalization_id');
    // Clear cookies
    document.cookie = 'access_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    document.cookie = 'x_personalization_id=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    document.cookie = 'user_data=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  };
  
  const handleRefresh = () => {
    fetchPosts(topic, true);
  };
  
  const handleTwitterApiLogin = () => {
    // Store credentials in sessionStorage (more secure than localStorage)
    sessionStorage.setItem('twitterapi_creds', JSON.stringify(twitterApiCreds));
    
    setTwitterApiUser({
      username: twitterApiCreds.username,
      loginType: 'twitterapi'
    });
    
    setShowTwitterApiLogin(false);
    
    // Clear the form
    setTwitterApiCreds({
      username: '',
      email: '',
      password: '',
      apiKey: '',
      proxy: '',
      totpSecret: ''
    });
  };
  
  const handleTwitterApiLogout = () => {
    setTwitterApiUser(null);
    sessionStorage.removeItem('twitterapi_creds');
  };
  
  const handleShowReplies = async (postId, event) => {
    event.stopPropagation();
    
    // Toggle replies visibility
    if (showRepliesFor.has(postId)) {
      setShowRepliesFor(prev => {
        const newSet = new Set(prev);
        newSet.delete(postId);
        return newSet;
      });
      return;
    }
    
    // If already loading, don't trigger again
    if (loadingRepliesFor.has(postId)) {
      return;
    }
    
    // Mark as loading
    setLoadingRepliesFor(prev => new Set([...prev, postId]));
    
    try {
      // Use fetchRepliesForPost for Lists tab (5+ likes, top 5 replies)
      const replies = await fetchRepliesForPost(postId);
      
      if (replies.length > 0) {
        setPostReplies(prev => ({
          ...prev,
          [postId]: replies
        }));
        
        setShowRepliesFor(prev => new Set([...prev, postId]));
      }
    } catch (error) {
      console.error('Error fetching replies:', error);
    } finally {
      setLoadingRepliesFor(prev => {
        const newSet = new Set(prev);
        newSet.delete(postId);
        return newSet;
      });
    }
  };

  return (
    <div className="App">
      <nav className="nav">
        <div className="nav-left">
          <span>𝕏 Newspaper</span>
          {user && (
            <button 
              className="refresh-btn" 
              onClick={handleRefresh}
              title="Force refresh posts"
            >
              🔄 Refresh
            </button>
          )}
        </div>
        <div>
          <button 
            className={topic === 'news' ? 'active' : ''} 
            onClick={() => setTopic('news')}
          >
            News
          </button>
          <button 
            className={topic === 'sports' ? 'active' : ''} 
            onClick={() => setTopic('sports')}
          >
            Sports
          </button>
          <button 
            className={topic === 'entertainment' ? 'active' : ''} 
            onClick={() => setTopic('entertainment')}
          >
            Entertainment
          </button>
          <button 
            className={topic === 'memes' ? 'active' : ''} 
            onClick={() => setTopic('memes')}
          >
            Memes
          </button>
          <button 
            className={topic === 'finance' ? 'active' : ''} 
            onClick={() => setTopic('finance')}
          >
            Finance
          </button>
          <button 
            className={topic === 'lists' ? 'active' : ''} 
            onClick={() => setTopic('lists')}
          >
            Lists
          </button>
          <button 
            className={topic === 'pro' ? 'active pro-btn' : 'pro-btn'} 
            onClick={() => setTopic('pro')}
          >
            PRO
          </button>
        </div>
        {!user && !twitterApiUser && (
          <>
            <button onClick={handleLogin}>Login with X</button>
            <button onClick={() => setShowTwitterApiLogin(true)} className="twitterapi-login-btn">
              Login with Twitterapi.io
            </button>
          </>
        )}
        {user && (
          <div className="user-info">
            {user.profileImage && (
              <img 
                src={user.profileImage} 
                alt={user.name}
                className="user-avatar"
              />
            )}
            <span>@{user.name}</span>
            <button onClick={() => setShowLogs(!showLogs)}>
              {showLogs ? 'Hide' : 'Show'} Logs
            </button>
            <button onClick={handleLogout}>Logout</button>
          </div>
        )}
        {twitterApiUser && !user && (
          <div className="user-info twitterapi-user">
            <div className="twitterapi-badge">🔧</div>
            <span>@{twitterApiUser.username}</span>
            <span className="login-type-badge">Twitterapi.io</span>
            <button onClick={handleTwitterApiLogout}>Logout</button>
          </div>
        )}
      </nav>
      
      {/* Twitterapi.io Login Modal */}
      {showTwitterApiLogin && (
        <div className="modal-overlay" onClick={() => setShowTwitterApiLogin(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Login with Twitterapi.io</h2>
              <button className="modal-close" onClick={() => setShowTwitterApiLogin(false)}>×</button>
            </div>
            <div className="modal-body">
              <p className="modal-description">
                Enter your Twitter credentials and proxy to enable full interaction features (reply, like, repost).
                <br/><strong>Note:</strong> A proxy is REQUIRED by twitterapi.io for all write operations.
              </p>
              <form onSubmit={(e) => {
                e.preventDefault();
                handleTwitterApiLogin();
              }}>
                <div className="form-group">
                  <label htmlFor="twitter-username">Twitter Username <span style={{color: 'red'}}>*</span></label>
                  <input
                    id="twitter-username"
                    type="text"
                    value={twitterApiCreds.username}
                    onChange={(e) => setTwitterApiCreds({...twitterApiCreds, username: e.target.value})}
                    placeholder="@username or username"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="twitter-email">Twitter Email <span style={{color: 'red'}}>*</span></label>
                  <input
                    id="twitter-email"
                    type="email"
                    value={twitterApiCreds.email}
                    onChange={(e) => setTwitterApiCreds({...twitterApiCreds, email: e.target.value})}
                    placeholder="your@email.com"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="twitter-password">Twitter Password <span style={{color: 'red'}}>*</span></label>
                  <input
                    id="twitter-password"
                    type="password"
                    value={twitterApiCreds.password}
                    onChange={(e) => setTwitterApiCreds({...twitterApiCreds, password: e.target.value})}
                    placeholder="Your password"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="proxy">Proxy <span style={{color: 'red'}}>*</span></label>
                  <input
                    id="proxy"
                    type="text"
                    value={twitterApiCreds.proxy}
                    onChange={(e) => setTwitterApiCreds({...twitterApiCreds, proxy: e.target.value})}
                    placeholder="http://username:password@ip:port"
                    required
                  />
                  <small>Residential proxy required. Get from <a href="https://www.webshare.io/?referral_code=4e0q1n00a504" target="_blank" rel="noopener noreferrer">Webshare.io</a></small>
                </div>
                <div className="form-group">
                  <label htmlFor="api-key">Twitterapi.io API Key <span style={{color: 'red'}}>*</span></label>
                  <input
                    id="api-key"
                    type="text"
                    value={twitterApiCreds.apiKey}
                    onChange={(e) => setTwitterApiCreds({...twitterApiCreds, apiKey: e.target.value})}
                    placeholder="Your API key"
                    required
                  />
                  <small>Get your API key from <a href="https://twitterapi.io" target="_blank" rel="noopener noreferrer">twitterapi.io</a></small>
                </div>
                <div className="form-group">
                  <label htmlFor="totp-secret">2FA/TOTP Secret <span style={{color: 'red'}}>*</span></label>
                  <input
                    id="totp-secret"
                    type="text"
                    value={twitterApiCreds.totpSecret}
                    onChange={(e) => setTwitterApiCreds({...twitterApiCreds, totpSecret: e.target.value})}
                    placeholder="Your 2FA secret key"
                    required
                  />
                  <small>Required if 2FA is enabled on your Twitter account</small>
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn-secondary" onClick={() => setShowTwitterApiLogin(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary">
                    Login
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      
      {showLogs && logs.length > 0 && (
        <div className="logs-panel">
          <div className="logs-header">
            <strong>Fetch Logs</strong> - Source: <span className={`source-badge source-${source}`}>{source}</span>
            <button onClick={() => setShowLogs(false)} className="close-logs">×</button>
          </div>
          <div className="logs-content">
            {logs.map((log, i) => (
              <div key={i} className="log-entry">{log}</div>
            ))}
          </div>
        </div>
      )}
      {topic === 'lists' && (
        <div className="lists-selector">
          <h3>📋 Select a List</h3>
          <div className="list-selection-container">
            <div className="list-dropdown-section">
              <label htmlFor="curated-lists">Curated Lists:</label>
              <select 
                id="curated-lists"
                value={selectedList || ''} 
                onChange={(e) => setSelectedList(e.target.value)}
                className="list-dropdown"
              >
                {popularLists.map(list => (
                  <option key={list.id} value={list.id}>
                    {list.name} by @{list.owner_username} ({list.subscriber_count?.toLocaleString() || 0} followers)
                  </option>
                ))}
              </select>
            </div>
            
            <div className="manual-list-section">
              <label htmlFor="manual-list">Or enter List ID manually:</label>
              <div className="manual-list-input-group">
                <input
                  id="manual-list"
                  type="text"
                  value={manualListId}
                  onChange={(e) => setManualListId(e.target.value)}
                  placeholder="Enter list ID (e.g., 12345678)"
                  className="manual-list-input"
                />
                <button 
                  onClick={() => manualListId && setSelectedList(manualListId)}
                  disabled={!manualListId}
                  className="load-list-btn"
                >
                  Load
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {topic === 'pro' && (
        <div className="pro-view-selector">
          <h3>✨ PRO Multi-List View</h3>
          <div className="pro-custom-list-section">
            <label htmlFor="pro-custom-list">Add 6th List (Optional):</label>
            <div className="pro-custom-list-input-group">
              <input
                id="pro-custom-list"
                type="text"
                value={proCustomListId}
                onChange={(e) => setProCustomListId(e.target.value)}
                placeholder="Enter list ID"
                className="pro-custom-list-input"
              />
              <button 
                onClick={handleAddProCustomList}
                disabled={!proCustomListId || loadingProLists || Object.keys(proLists).length >= 6}
                className="add-pro-list-btn"
              >
                {loadingProLists ? 'Adding...' : 'Add List'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {topic === 'pro' && loadingProLists ? (
        <div className="loading">Loading PRO lists...</div>
      ) : topic === 'pro' && Object.keys(proLists).length > 0 ? (
        <div className="pro-lists-container">
          {Object.entries(proLists).map(([listId, listData]) => (
            <div key={listId} className="pro-list-column">
              <div className="pro-list-header">
                <h4>{listData.listInfo.name}</h4>
                <span className="pro-list-owner">@{listData.listInfo.owner_username}</span>
              </div>
              <div className="pro-list-posts">
                {listData.posts.map(post => (
                  <div 
                    key={post.id} 
                    className="pro-post clickable-post"
                    onClick={(e) => openOnX(post.id, post.author?.username, e)}
                  >
                    <p className="post-text">{post.text}</p>
                    {post.media && post.media.length > 0 ? (
                      <TweetMedia media={post.media} />
                    ) : post.image ? (
                      <img 
                        src={post.image} 
                        alt="post media" 
                        className="post-media"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    ) : null}
                    
                    <div className="post-stats">
                      <div className="post-stats-left">
                        <div 
                          className="stat-item clickable"
                          onClick={(e) => handleReply(e, post.id)}
                          title={user ? 'Reply' : 'Reply on X'}
                        >
                          <span className="stat-icon">💬</span>
                          <span>{post.replies?.toLocaleString() || 0}</span>
                        </div>
                        <div className="stat-item">
                          <span className="stat-icon">🔁</span>
                          <span>{post.retweets?.toLocaleString() || 0}</span>
                        </div>
                        <div 
                          className={`stat-item clickable ${likedPosts.has(post.id) ? 'liked' : ''}`}
                          onClick={(e) => (user || twitterApiUser) && handleLike(post.id, e)}
                          style={{ cursor: (user || twitterApiUser) ? 'pointer' : 'default' }}
                        >
                          <span className="stat-icon">❤️</span>
                          <span>{post.likes?.toLocaleString() || 0}</span>
                        </div>
                      </div>
                      {post.createdAt && (
                        <div className="post-timestamp">{formatTimestamp(post.createdAt)}</div>
                      )}
                    </div>
                    
                    {replyingTo === post.id && (user || twitterApiUser) && (
                      <div className="reply-box" onClick={(e) => e.stopPropagation()}>
                        <div className="textarea-wrapper">
                          <textarea
                            className="reply-textarea"
                            placeholder="Write your reply... (Type :emoji: for autocomplete)"
                            value={replyText}
                            onChange={handleReplyTextChange}
                            onKeyDown={handleEmojiKeyDown}
                            maxLength={280}
                            rows={2}
                          />
                          
                          {emojiSuggestions.length > 0 && (
                            <div className="emoji-picker">
                              {emojiSuggestions.map((item, index) => (
                                <div
                                  key={item.name}
                                  className={`emoji-item ${index === selectedEmojiIndex ? 'selected' : ''}`}
                                  onClick={() => insertEmoji(item.emoji)}
                                >
                                  <span className="emoji-char">{item.emoji}</span>
                                  <span className="emoji-name">:{item.name}:</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        {replyMediaPreview && (
                          <div className="reply-media-preview">
                            <img src={replyMediaPreview} alt="Upload preview" />
                            <button 
                              className="remove-media-btn"
                              onClick={handleRemoveMedia}
                              title="Remove image"
                            >
                              ✕
                            </button>
                          </div>
                        )}
                        
                        <div className="reply-actions">
                          <div className="reply-left-actions">
                            <label className="media-upload-btn" title="Add image">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handleMediaSelect}
                                style={{ display: 'none' }}
                              />
                              🖼️
                            </label>
                            <span className="char-count">{replyText.length}/280</span>
                          </div>
                          <button 
                            className="reply-send-btn"
                            onClick={(e) => handleSendReply(e, post.id)}
                            disabled={!replyText.trim() || replyStatus === 'sending'}
                          >
                            {replyStatus === 'sending' ? 'Sending...' : 'Reply'}
                          </button>
                        </div>
                        {replyStatus === 'success' && (
                          <div className="reply-notification success">
                            ✓ Reply sent successfully!
                          </div>
                        )}
                        {replyStatus === 'error' && (
                          <div className="reply-notification error">
                            ✗ Failed to send reply. Please try again.
                          </div>
                        )}
                      </div>
                    )}
                    
                    {post.author && (
                      <div className="post-author">
                        {post.author.profileImage && (
                          <img 
                            src={post.author.profileImage} 
                            alt={post.author.name}
                            className="author-avatar"
                          />
                        )}
                        <div className="author-info">
                          <span className="author-name">{post.author.name}</span>
                          <span 
                            className="author-username clickable"
                            onClick={(e) => {
                              e.stopPropagation();
                              openOnX(post.id, post.author.username, e);
                            }}
                            title="Open on X.com"
                          >
                            @{post.author.username}
                          </span>
                        </div>
                      </div>
                    )}
                    
                    {!showRepliesFor.has(post.id) && !postReplies[post.id] && (
                      <button
                        className="show-replies-btn"
                        onClick={(e) => handleShowReplies(post.id, e)}
                        disabled={loadingRepliesFor.has(post.id)}
                      >
                        {loadingRepliesFor.has(post.id) ? 'Loading replies...' : '💬 Show Replies'}
                      </button>
                    )}
                    
                    {(showRepliesFor.has(post.id) || postReplies[post.id]) && postReplies[post.id] && postReplies[post.id].length > 0 && (
                      <div className="post-replies">
                        {postReplies[post.id].map(reply => (
                          <div 
                            key={reply.id} 
                            className="reply-item"
                          >
                            <div className="reply-header">
                              {reply.author.profileImage && (
                                <img 
                                  src={reply.author.profileImage} 
                                  alt={reply.author.name}
                                  className="reply-avatar"
                                />
                              )}
                              <div className="reply-author-info">
                                <span className="reply-author-name">{reply.author.name}</span>
                                <span 
                                  className="reply-author-username clickable"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openOnX(reply.id, reply.author.username, e);
                                  }}
                                >
                                  @{reply.author.username}
                                </span>
                              </div>
                            </div>
                            <p className="reply-text">{reply.text}</p>
                            <div className="reply-stats">
                              <div className="reply-stats-left">
                                <span 
                                  className="reply-stat clickable"
                                  onClick={(e) => handleReply(e, reply.id)}
                                  title={user ? 'Reply' : 'Reply on X'}
                                >
                                  <span className="stat-icon">💬</span> {reply.replies?.toLocaleString() || 0}
                                </span>
                                <span className="reply-stat">
                                  <span className="stat-icon">🔁</span> {reply.retweets?.toLocaleString() || 0}
                                </span>
                                <span className="reply-stat">
                                  <span className="stat-icon">❤️</span> {reply.likes?.toLocaleString() || 0}
                                </span>
                              </div>
                              {reply.createdAt && (
                                <div className="reply-timestamp" title={new Date(reply.createdAt).toLocaleString()}>
                                  {formatTimestamp(reply.createdAt)}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : null}
      
      {loading && topic !== 'pro' ? (
        <div className="loading">Loading posts...</div>
      ) : topic !== 'pro' ? (
        <div className="posts-grid">
          {posts.map(post => {
            const isViral = post.likes > 1000;
            const isShort = post.text.split(/\s+/).length <= 50;
            
            // Debug: Log if post has image
            if (post.image) {
              console.log(`Post ${post.id} has image:`, post.image);
            }
            
            return (
              <div 
                key={post.id} 
                className={`post ${post.size} ${isViral && isShort ? 'viral' : ''} clickable-post`}
                onClick={(e) => openOnX(post.id, post.author?.username, e)}
              >
                <p className="post-text">{post.text}</p>
                {post.media && post.media.length > 0 ? (
                  <TweetMedia media={post.media} />
                ) : post.image ? (
                  <img 
                    src={post.image} 
                    alt="post media" 
                    className="post-media"
                    onError={(e) => {
                      console.error('Failed to load image:', post.image);
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (() => {
                  // Extract t.co URLs from tweet text as fallback
                  const tcoRegex = /https?:\/\/t\.co\/[a-zA-Z0-9]+/g;
                  const urls = post.text.match(tcoRegex);
                  if (urls && urls.length > 0) {
                    return (
                      <img 
                        src={urls[0]} 
                        alt="tweet media" 
                        className="post-media"
                        onError={(e) => {
                          console.error('Failed to load t.co image:', urls[0]);
                          e.target.style.display = 'none';
                        }}
                      />
                    );
                  }
                  return null;
                })()}
                
                {post.quotedStatus && (
                  <div className="react-tweet-container" onClick={(e) => e.stopPropagation()}>
                    <Tweet id={post.quotedStatus.id} />
                  </div>
                )}
                
                <div className="post-stats">
                  <div className="post-stats-left">
                    <div 
                      className="stat-item clickable"
                      onClick={(e) => handleReply(e, post.id)}
                      title={user ? 'Reply' : 'Reply on X'}
                    >
                      <span className="stat-icon">💬</span>
                      <span>{post.replies?.toLocaleString() || 0}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-icon">🔁</span>
                      <span>{post.retweets?.toLocaleString() || 0}</span>
                    </div>
                    <div 
                      className={`stat-item clickable ${likedPosts.has(post.id) ? 'liked' : ''}`}
                      onClick={(e) => (user || twitterApiUser) && handleLike(post.id, e)}
                      style={{ cursor: (user || twitterApiUser) ? 'pointer' : 'default' }}
                      title={(user || twitterApiUser) ? 'Like' : 'Login to like'}
                    >
                      <span className="stat-icon">❤️</span>
                      <span className="like-count">{post.likes?.toLocaleString() || 0}</span>
                    </div>
                  </div>
                  {post.createdAt && (
                    <div className="post-timestamp" title={new Date(post.createdAt).toLocaleString()}>
                      {formatTimestamp(post.createdAt)}
                    </div>
                  )}
                </div>
                
                {replyingTo === post.id && (user || twitterApiUser) && (
                  <div className="reply-box" onClick={(e) => e.stopPropagation()}>
                    <div className="textarea-wrapper">
                      <textarea
                        className="reply-textarea"
                        placeholder="Write your reply... (Type :emoji: for autocomplete)"
                        value={replyText}
                        onChange={handleReplyTextChange}
                        onKeyDown={handleEmojiKeyDown}
                        maxLength={280}
                        rows={3}
                      />
                      
                      {emojiSuggestions.length > 0 && (
                        <div className="emoji-picker">
                          {emojiSuggestions.map((item, index) => (
                            <div
                              key={item.name}
                              className={`emoji-item ${index === selectedEmojiIndex ? 'selected' : ''}`}
                              onClick={() => insertEmoji(item.emoji)}
                            >
                              <span className="emoji-char">{item.emoji}</span>
                              <span className="emoji-name">:{item.name}:</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    {replyMediaPreview && (
                      <div className="reply-media-preview">
                        <img src={replyMediaPreview} alt="Upload preview" />
                        <button 
                          className="remove-media-btn"
                          onClick={handleRemoveMedia}
                          title="Remove image"
                        >
                          ✕
                        </button>
                      </div>
                    )}
                    
                    <div className="reply-actions">
                      <div className="reply-left-actions">
                        <label className="media-upload-btn" title="Add image">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleMediaSelect}
                            style={{ display: 'none' }}
                          />
                          🖼️
                        </label>
                        <span className="char-count">{replyText.length}/280</span>
                      </div>
                      <button 
                        className="reply-send-btn"
                        onClick={(e) => handleSendReply(e, post.id)}
                        disabled={!replyText.trim() || replyStatus === 'sending'}
                      >
                        {replyStatus === 'sending' ? 'Sending...' : 'Reply'}
                      </button>
                    </div>
                    {replyStatus === 'success' && (
                      <div className="reply-notification success">
                        ✓ Reply sent successfully!
                      </div>
                    )}
                    {replyStatus === 'error' && (
                      <div className="reply-notification error">
                        ✗ Failed to send reply. Please try again.
                      </div>
                    )}
                  </div>
                )}
                
                {post.author && (
                  <div className="post-author">
                    {post.author.profileImage && (
                      <img 
                        src={post.author.profileImage} 
                        alt={post.author.name}
                        className="author-avatar"
                      />
                    )}
                    <div className="author-info">
                      <span className="author-name">{post.author.name}</span>
                      <span 
                        className="author-username clickable"
                        onClick={(e) => {
                          e.stopPropagation();
                          openOnX(post.id, post.author.username, e);
                        }}
                        title="Open on X.com"
                      >
                        @{post.author.username}
                      </span>
                    </div>
                  </div>
                )}
                
                {topic === 'lists' && !showRepliesFor.has(post.id) && !postReplies[post.id] && (
                  <button
                    className="show-replies-btn"
                    onClick={(e) => handleShowReplies(post.id, e)}
                    disabled={loadingRepliesFor.has(post.id)}
                  >
                    {loadingRepliesFor.has(post.id) ? 'Loading replies...' : '💬 Show Replies'}
                  </button>
                )}
                
                {(showRepliesFor.has(post.id) || postReplies[post.id]) && postReplies[post.id] && postReplies[post.id].length > 0 && (
                  <div className="post-replies">
                    {postReplies[post.id].map(reply => (
                      <div 
                        key={reply.id} 
                        className="reply-item"
                      >
                        <div className="reply-header">
                          {reply.author.profileImage && (
                            <img 
                              src={reply.author.profileImage} 
                              alt={reply.author.name}
                              className="reply-avatar"
                            />
                          )}
                          <div className="reply-author-info">
                            <span className="reply-author-name">{reply.author.name}</span>
                            <span 
                              className="reply-author-username clickable"
                              onClick={(e) => {
                                e.stopPropagation();
                                openOnX(reply.id, reply.author.username, e);
                              }}
                            >
                              @{reply.author.username}
                            </span>
                          </div>
                        </div>
                        <p className="reply-text">{reply.text}</p>
                        <div className="reply-stats">
                          <div className="reply-stats-left">
                            <span 
                              className="reply-stat clickable"
                              onClick={(e) => handleReply(e, reply.id)}
                              title={user ? 'Reply' : 'Reply on X'}
                            >
                              <span className="stat-icon">💬</span> {reply.replies?.toLocaleString() || 0}
                            </span>
                            <span className="reply-stat">
                              <span className="stat-icon">🔁</span> {reply.retweets?.toLocaleString() || 0}
                            </span>
                            <span className="reply-stat">
                              <span className="stat-icon">❤️</span> {reply.likes?.toLocaleString() || 0}
                            </span>
                          </div>
                          {reply.createdAt && (
                            <div className="reply-timestamp" title={new Date(reply.createdAt).toLocaleString()}>
                              {formatTimestamp(reply.createdAt)}
                            </div>
                          )}
                        </div>
                        
                        {replyingTo === reply.id && (user || twitterApiUser) && (
                          <div className="reply-box" onClick={(e) => e.stopPropagation()}>
                            <div className="textarea-wrapper">
                              <textarea
                                className="reply-textarea"
                                placeholder="Write your reply... (Type :emoji: for autocomplete)"
                                value={replyText}
                                onChange={handleReplyTextChange}
                                onKeyDown={handleEmojiKeyDown}
                                maxLength={280}
                                rows={3}
                              />
                              
                              {emojiSuggestions.length > 0 && (
                                <div className="emoji-picker">
                                  {emojiSuggestions.map((item, index) => (
                                    <div
                                      key={item.name}
                                      className={`emoji-item ${index === selectedEmojiIndex ? 'selected' : ''}`}
                                      onClick={() => insertEmoji(item.emoji)}
                                    >
                                      <span className="emoji-char">{item.emoji}</span>
                                      <span className="emoji-name">:{item.name}:</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                            
                            {replyMediaPreview && (
                              <div className="reply-media-preview">
                                <img src={replyMediaPreview} alt="Upload preview" />
                                <button 
                                  className="remove-media-btn"
                                  onClick={handleRemoveMedia}
                                  title="Remove image"
                                >
                                  ✕
                                </button>
                              </div>
                            )}
                            
                            <div className="reply-actions">
                              <div className="reply-left-actions">
                                <label className="media-upload-btn" title="Add image">
                                  <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleMediaSelect}
                                    style={{ display: 'none' }}
                                  />
                                  🖼️
                                </label>
                                <span className="char-count">{replyText.length}/280</span>
                              </div>
                              <button 
                                className="reply-send-btn"
                                onClick={(e) => handleSendReply(e, reply.id)}
                                disabled={!replyText.trim() || replyStatus === 'sending'}
                              >
                                {replyStatus === 'sending' ? 'Sending...' : 'Reply'}
                              </button>
                            </div>
                            {replyStatus === 'success' && (
                              <div className="reply-notification success">
                                ✓ Reply sent successfully!
                              </div>
                            )}
                            {replyStatus === 'error' && (
                              <div className="reply-notification error">
                                ✗ Failed to send reply. Please try again.
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export default App;
